import * as React from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send, ShieldAlert, AlertCircle, Loader2, Copy } from "lucide-react";
import { explainWithAI } from "@/lib/ai";
import { useAuth } from "@/supabase/AuthContext";

const examplePrompts = [
  "En son satış aktarımındaki en çok satan ürünleri ve satış tutarlarını özetle.",
  "Vadesi gelmemiş açık çekleri vade tarihi, çek numarası, banka ve tutarlarıyla göster.",
  "Beşiktaş cari hesabında ne kadar borcumuz var? Ödemeleri ve verilen çekleri de hesaba katarak açıkla.",
  "Stok miktarlarını ve maliyeti belirsiz ürünleri değerlendir; eksik kapsamı belirt.",
  "Gerçek Beşiktaş alış faturalarının tamamını ve Nebim envanterini tara. Beden/renk ayrıntılarını ana ürün maliyeti için zorunlu tutmadan, ürün bazında KDV hariç ağırlıklı ortalama alış maliyetlerini ve eşleşmeyenleri göster.",
  "İşletme giderlerini aylara göre özetle ve satış verisiyle birlikte değerlendirme yap.",
];

const AIAnalysis: React.FC = () => {
  const { companyId } = useAuth();
  const [prompt, setPrompt] = React.useState("");
  const [answer, setAnswer] = React.useState("");
  const [model, setModel] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  const sendPrompt = async () => {
    if (!prompt.trim() || loading) return;
    setLoading(true);
    setError(null);
    setAnswer("");
    setModel("");
    try {
      const result = await explainWithAI(prompt.trim(), companyId ?? "");
      if (!result.ok) {
        setError(result.message);
        return;
      }
      setAnswer(result.text);
      setModel(result.model);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Yapay zekâ isteği sırasında beklenmeyen bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-ai-assistant" className="space-y-8">
      <PageHeader
        eyebrow="Akıllı Katman"
        title="Yapay Zekâ Asistanı"
        description="Satış, stok, cari hesap, çek, test faturası ve işletme giderlerini doğal dille sorgulayın; asistan veri kapsamını belirterek yanıtlar."
      />

      <section className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
        <strong>Veri erişimi:</strong> Sorunuzla birlikte aktif şirketinizin yetkili satış ve stok kayıtları, cari hesap hareketleri, açık çekler, Beşiktaş alış faturaları ve işletme giderlerinin ilgili özetleri sunucu tarafından okunarak yapay zekâ hizmetine gönderilir. Hassas bilgiler gereksiz yere aktarılmaz. Test faturaları hariç gerçek Beşiktaş alış faturalarının taslak ve kesinleşmiş satırları ürün maliyeti için okunur. Taslak faturalar stok veya cari hareketi olarak kabul edilmez. Finansal kayıtlar değiştirilemez; asistan yalnızca okur ve yorumlar.
      </section>

      <section aria-label="Örnek sorular" className="space-y-3">
        <div className="eyebrow">Örnek istemler</div>
        <div className="flex flex-wrap gap-2">
          {examplePrompts.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setPrompt(item)}
              className="rounded-full border border-border bg-card px-3.5 py-1.5 text-left text-sm text-foreground/80 hover:bg-secondary"
            >
              {item}
            </button>
          ))}
        </div>
      </section>

      <section aria-label="Yapay zekâ sohbeti" className="rounded-lg border border-border bg-card">
        <div className="border-b border-border px-5 py-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-accent" />
            <h2 className="font-serif text-xl text-foreground">Analiz isteği</h2>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Doğrudan istediğiniz soruyu yazabilirsiniz (ör. “Vadesi gelmemiş çekleri göster” veya “Beşiktaş’a ne kadar borcumuz var?”). Asistan yalnızca aktif şirketin okuyabildiği kayıtları kullanır. İstem başına en fazla 20.000 karakter gönderilir.
          </p>
        </div>
        <div className="space-y-4 p-5">
          <Textarea
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder="Çekler, cari hesaplar, satış, stok veya giderler hakkında sorunuzu yazın…"
            className="min-h-[160px] resize-y"
            maxLength={20000}
            data-testid="ai-assistant-input"
            aria-label="Yapay zekâ asistanı isteği"
          />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">{prompt.length.toLocaleString("tr-TR")} / 20.000 karakter</span>
            <Button onClick={() => void sendPrompt()} disabled={loading || !prompt.trim()} data-testid="ai-assistant-send-button">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              {loading ? "Analiz ediliyor…" : "Analiz et"}
            </Button>
          </div>
          {error ? (
            <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              <AlertCircle className="mr-2 inline h-4 w-4" />{error}
            </div>
          ) : null}
        </div>
        {answer ? (
          <div className="border-t border-border p-5">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 font-medium text-foreground"><Sparkles className="h-4 w-4 text-accent" />Yapay zekâ yanıtı <span className="text-xs font-normal text-muted-foreground">{model}</span></div>
              <Button variant="outline" size="sm" onClick={() => { void navigator.clipboard?.writeText(answer); }}>
                <Copy className="mr-2 h-4 w-4" /> Kopyala
              </Button>
            </div>
            <div className="whitespace-pre-wrap text-sm leading-7 text-foreground">{answer}</div>
          </div>
        ) : (
          <div className="border-t border-border px-5 py-6 text-sm text-muted-foreground">
            Henüz yanıt yok. İsteğinizi gönderdiğinizde sonuç burada görünür.
          </div>
        )}
      </section>

      <div className="flex items-start gap-2 text-xs text-muted-foreground">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
        <span>Modelin önerileri tek başına finansal karar değildir. Ciro, KDV, stok ve kârlılık hesapları doğrulanmış verilerle uygulama tarafından yapılmalıdır.</span>
      </div>
    </div>
  );
};

export default AIAnalysis;
